import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';
import { SSO_ERROR_VALUES, type SsoConfigDto, type SsoError } from '@fas/types';
import { emailSchema, secondFactorCodeSchema } from '@fas/validation';
import { Alert, Button, Field, Input } from '@fas/ui';
import { ApiError, apiRequest, buildUrl } from '@/lib/api';
import { signIn, signInWithCode } from '@/lib/session';
import { AuthLayout } from './AuthLayout';

const schema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required').max(128),
});
type Values = z.infer<typeof schema>;

export function loginErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'INVALID_CREDENTIALS':
        return 'The email or password is incorrect.';
      case 'ACCOUNT_LOCKED': {
        const seconds = Number(err.details?.retryAfterSeconds ?? 0);
        const minutes = Math.max(1, Math.ceil(seconds / 60));
        return `Too many failed attempts. Your account is locked for about ${minutes} minute${minutes === 1 ? '' : 's'}, or reset your password.`;
      }
      case 'ACCOUNT_INACTIVE':
        return 'This account is not active. Contact your administrator.';
      case 'RATE_LIMITED':
        return 'Too many sign-in attempts from this device. Please wait a minute and try again.';
      case 'NETWORK_ERROR':
        return 'Cannot reach the server. Check your internet connection and try again.';
      case 'MFA_CODE_INVALID':
        return 'That code is not right. Use the newest code from your authenticator app, or a recovery code you have not used yet.';
      case 'MFA_TOKEN_INVALID':
        return 'This sign-in took too long or is no longer valid. Please sign in again.';
      case 'MFA_NOT_AVAILABLE':
        return 'Two-step sign-in is not available on the server right now. Contact your administrator.';
      default:
        return err.message;
    }
  }
  return 'Sign-in failed. Please try again.';
}

/** Why single sign-on sent the browser back here (Phase 3E). */
export const SSO_ERROR_MESSAGES: Record<SsoError, string> = {
  cancelled: 'Single sign-on was cancelled. Try again, or sign in with your password.',
  expired: 'The single sign-on attempt expired or was already used. Please try again.',
  no_account: 'No account here matches your sign-in. Ask your administrator to set one up for you.',
  inactive: 'This account is not active. Contact your administrator.',
  unverified_email: 'Your identity provider did not confirm your e-mail address. Contact your administrator.',
  failed: 'Single sign-on did not work this time. Please try again.',
};

function ssoErrorOf(search: string): string | null {
  const code = new URLSearchParams(search).get('sso_error');
  return code && (SSO_ERROR_VALUES as readonly string[]).includes(code) ? SSO_ERROR_MESSAGES[code as SsoError] : null;
}

/** Whether (and as what) single sign-on is offered; nothing is shown while the server cannot say. */
function useSsoConfig(): SsoConfigDto | null {
  const [config, setConfig] = useState<SsoConfigDto | null>(null);
  useEffect(() => {
    let current = true;
    apiRequest<SsoConfigDto>('GET', '/auth/sso', { auth: false })
      .then((r) => current && setConfig(r.data))
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, []);
  return config;
}

const codeSchema = z.object({ code: secondFactorCodeSchema });

/** The second step of a password sign-in: a code from the authenticator app, or a recovery code (ADR-068). */
function CodeStep({ mfaToken, onSignedIn, onStartOver }: { mfaToken: string; onSignedIn: () => void; onStartOver: (message: string | null) => void }) {
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<{ code: string }>({ resolver: zodResolver(codeSchema), defaultValues: { code: '' } });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async ({ code }) => {
    setError(null);
    try {
      await signInWithCode(mfaToken, code);
      onSignedIn();
    } catch (err) {
      // An expired attempt or a locked account cannot go on with this code: back to the password.
      if (err instanceof ApiError && (err.code === 'MFA_TOKEN_INVALID' || err.code === 'ACCOUNT_LOCKED')) {
        onStartOver(loginErrorMessage(err));
        return;
      }
      setError(loginErrorMessage(err));
      form.reset({ code: '' });
      form.setFocus('code');
    }
  });

  const toggle = () => {
    setRecovery((r) => !r);
    setError(null);
    form.reset({ code: '' });
  };

  return (
    <AuthLayout title="Two-step sign-in" subtitle={recovery ? 'Enter one of your recovery codes' : 'Enter the code from your authenticator app'}>
      <form noValidate onSubmit={onSubmit} className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <Field
          id="login-code"
          label={recovery ? 'Recovery code' : 'Six-digit code'}
          error={errors.code?.message}
          hint={recovery ? 'Each recovery code works once. You saved them when you turned on two-step sign-in.' : 'The code changes every 30 seconds.'}
          required
        >
          {(a11y) => (
            <Input
              key={recovery ? 'recovery' : 'totp'}
              {...a11y}
              autoComplete="one-time-code"
              inputMode={recovery ? 'text' : 'numeric'}
              autoCapitalize={recovery ? 'characters' : 'off'}
              spellCheck={false}
              maxLength={recovery ? 20 : 6}
              autoFocus
              {...form.register('code')}
            />
          )}
        </Field>
        <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>
          Verify
        </Button>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <Button type="button" variant="link" size="sm" className="px-0" onClick={toggle}>
            {recovery ? 'Use the authenticator app' : 'Use a recovery code'}
          </Button>
          <Button type="button" variant="link" size="sm" className="px-0" onClick={() => onStartOver(null)}>
            Start over
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [error, setError] = useState<string | null>(() => ssoErrorOf(location.search));
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const sso = useSsoConfig();
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '', password: '' } });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const result = await signIn(values.email, values.password);
      if (result.status === 'code-required') {
        setMfaToken(result.mfaToken);
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(loginErrorMessage(err));
    }
  });

  if (mfaToken) {
    return (
      <CodeStep
        mfaToken={mfaToken}
        onSignedIn={() => navigate(from, { replace: true })}
        onStartOver={(message) => {
          setMfaToken(null);
          form.resetField('password');
          setError(message);
        }}
      />
    );
  }

  return (
    <AuthLayout title="Sign in" subtitle="Use your institutional email address">
      <div className="space-y-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}
        {sso?.enabled ? (
          <div className="space-y-3">
            <Button asChild size="lg" variant="outline" className="w-full">
              <a href={buildUrl('/auth/oidc/start', { returnTo: from })}>Continue with {sso.displayName}</a>
            </Button>
            {!sso.passwordForEveryone ? <p className="text-center text-xs text-slate-600">Most accounts sign in this way; the password below is for accounts that keep one.</p> : null}
            <p className="flex items-center gap-3 text-xs uppercase tracking-wide text-slate-500" aria-hidden>
              <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
            </p>
          </div>
        ) : null}
        <form noValidate onSubmit={onSubmit} className="space-y-4">
          <Field id="login-email" label="Email" error={errors.email?.message} required>
            {(a11y) => <Input {...a11y} type="email" autoComplete="username" inputMode="email" autoFocus {...form.register('email')} />}
          </Field>
          <Field id="login-password" label="Password" error={errors.password?.message} required>
            {(a11y) => <Input {...a11y} type="password" autoComplete="current-password" {...form.register('password')} />}
          </Field>
          <Button type="submit" size="lg" className="w-full" loading={isSubmitting}>
            Sign in
          </Button>
          <p className="text-center text-sm">
            <Link to="/forgot-password" className="font-medium text-blue-700 hover:underline">
              Forgot your password?
            </Link>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
}
